import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MyPageSettingsLanguageScreen } from '../features/my-page-settings/ui/my-page-settings-language-screen';
import { MyPageSettingsPageLayout } from '../features/my-page-settings/ui/my-page-settings-page-layout';
import { updateEmployeeProfile } from '../shared/api/employee.service';
import {
  persistUserSelectedAppLanguage,
  resolveApiLocale,
  resolveAppLanguage,
  type AppLanguage,
} from '../shared/config';

export function MyPageLanguagePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { i18n, t } = useTranslation();
  const currentLanguage = resolveAppLanguage(i18n.language);
  const localeUpdate = useMutation({
    mutationFn: updateEmployeeProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employee'],
        refetchType: 'all',
      });
    },
  });

  const handleLanguageSelect = (language: AppLanguage) => {
    if (language === currentLanguage) {
      return;
    }

    persistUserSelectedAppLanguage(language);
    void i18n.changeLanguage(language);
    void localeUpdate
      .mutateAsync({
        locale: resolveApiLocale(language),
      })
      .catch(() => undefined);
  };

  return (
    <MyPageSettingsPageLayout
      backAriaLabel={t('myPage.backAria')}
      frameDataUi="settings-language-frame"
      onBack={() => {
        navigate('/settings');
      }}
      onOpenHome={() => {
        navigate('/home');
      }}
      onOpenMyPage={() => {
        navigate('/my-page');
      }}
      pageDataUi="settings-language"
      title={t('myPageSettings.language.title')}
    >
      <MyPageSettingsLanguageScreen
        currentLanguage={currentLanguage}
        onSelectLanguage={handleLanguageSelect}
      />
    </MyPageSettingsPageLayout>
  );
}
